import type { UseQueryResult } from "@tanstack/react-query";
import { useLocalization } from "@/context/localization-context";
import { Ago } from "../../components/primitives";
import { formatCount, formatPercent } from "../../format";
import type { BbSection, MetricSeries } from "../../types";
import { counterTotal, type Tone } from "../pipeline/helpers";
import { StatTile } from "../pipeline/shared";

/** below these shares of finished pizzas with a photo the coverage tile turns amber / red */
const COVERAGE_WARN = 0.98;
const COVERAGE_NOK = 0.95;

/** The numbers of the tab: photo coverage of finished pizzas, packing reports, webhook volume, the log. */
export function BbTotals({ data, metrics }: { data: BbSection; metrics: UseQueryResult<MetricSeries> }) {
  const { t } = useLocalization();
  const { finished, captured, total: missing } = data.uncaptured;
  const share = finished ? captured / finished : null;
  const coverageTone: Tone = share === null ? "neutral" : share < COVERAGE_NOK ? "nok" : share < COVERAGE_WARN ? "warn" : "neutral";
  const m = metrics.data;
  const received = m
    ? {
        orders: counterTotal(m, "bb.order"),
        items: counterTotal(m, "bb.item"),
        statuses: counterTotal(m, "bb.status"),
      }
    : null;
  const otherOrders = data.other_branches.reduce((sum, b) => sum + b.orders, 0);

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
      <StatTile
        label={t("monitoring.bb.summary.coverage")}
        hint={t("monitoring.bb.summary.coverageHint")}
        value={share === null ? "–" : formatPercent(share, 1)}
        tone={coverageTone}
        detail={t("monitoring.bb.summary.coverageDetail", {
          captured: formatCount(captured),
          finished: formatCount(finished),
          missing: formatCount(missing),
        })}
      />
      <StatTile
        label={t("monitoring.bb.summary.packed")}
        hint={t("monitoring.bb.summary.packedHint")}
        value={formatCount(data.packed_items)}
        tone={data.packed_not_captured > 0 ? "warn" : "neutral"}
        detail={t("monitoring.bb.summary.packedDetail", {
          captured: formatCount(data.captured_items),
          missing: formatCount(data.packed_not_captured),
        })}
      />
      <StatTile
        label={t("monitoring.bb.summary.received")}
        hint={t("monitoring.bb.summary.receivedHint")}
        value={received ? formatCount(received.orders + received.items + received.statuses) : "–"}
        detail={
          received
            ? t("monitoring.bb.summary.receivedDetail", {
                orders: formatCount(received.orders),
                items: formatCount(received.items),
                statuses: formatCount(received.statuses),
              })
            : undefined
        }
      />
      <StatTile
        label={t("monitoring.bb.summary.unmatched")}
        hint={t("monitoring.bb.summary.unmatchedHint")}
        value={formatCount(data.unmatched_items)}
        tone={data.unmatched_items > 0 ? "warn" : "neutral"}
        detail={t("monitoring.bb.summary.unmatchedDetail")}
      />
      <StatTile
        label={t("monitoring.bb.summary.other")}
        hint={t("monitoring.bb.summary.otherHint")}
        value={formatCount(data.other_branches_total)}
        detail={t("monitoring.bb.summary.otherDetail", { orders: formatCount(otherOrders) })}
      />
      <StatTile
        label={t("monitoring.bb.summary.log")}
        hint={t("monitoring.bb.summary.logHint", { days: data.log_ttl_days })}
        value={data.log_since ? <Ago ts={data.log_since} className="text-base" /> : "–"}
        detail={
          !data.persist
            ? t("monitoring.bb.summary.logMemory")
            : data.log_since
              ? t("monitoring.bb.summary.logKept", { days: data.log_ttl_days })
              : t("monitoring.bb.summary.logEmpty")
        }
      />
    </div>
  );
}
